import { useState } from "react";

import { Alert, Modal } from "@icg360/design-system";

import { MSSLink } from "components/common/link";
import { GENERAL_SUPPORT } from "consts";
import { useKeystone, useUser } from "data/ss-data-hooks";
import { isError } from "data/ss-error";
import { formatAddress, logException, trackEvent } from "utils";

import styles from "./unlink-policy-modal.module.scss";

type UnlinkPolicyModalProps = {
  handleClose: () => void;
  onSuccess: (string) => void;
  policyDetails?: {
    policyId: string;
    email: string;
    propertyAddress: { street1: string };
    isCommercial: boolean;
  };
  show: boolean;
};

const UnlinkPolicyModal = ({
  onSuccess,
  policyDetails,
  show,
  handleClose,
}: UnlinkPolicyModalProps) => {
  const [submitting, setSubmitting] = useState(false);
  const [unlinkError, setUnlinkError] = useState(false);
  const keystone = useKeystone();
  const user = useUser();

  const confirmedUnlink = async () => {
    if (!user) return;
    const { policyId = "" } = policyDetails ?? {};
    setSubmitting(true);
    setUnlinkError(false);

    trackEvent("Remove Policy Confirm Button Clicked", { policyId });

    const res = await keystone.removePolicy(user.email, policyId);
    if (isError(res)) {
      setUnlinkError(true);
      logException(res);
      trackEvent("Error Displayed (Remove Policy)", { error: res.message });
    } else {
      onSuccess(policyId);
    }
    setSubmitting(false);
  };

  return (
    <Modal
      open={show}
      onClose={handleClose}
      heading="Remove policy"
      primaryAction="Confirm and remove"
      onPrimaryAction={confirmedUnlink}
      loadingPrimary={submitting}
    >
      <div className={styles.content}>
        {unlinkError ? (
          <Alert
            appearance="danger"
            title="Something went wrong, please try again."
            description={
              <>
                If the issue persists, contact customer service at{" "}
                {GENERAL_SUPPORT}.
              </>
            }
            data-testid={"unlinkError"}
          />
        ) : null}
        <div className={styles.bold}>
          Are you sure you want to remove this policy from your account?
        </div>
        <div>{`${formatAddress(
          policyDetails?.propertyAddress,
          true,
        )} (Policy#: ${policyDetails?.policyId})`}</div>
        <Alert
          appearance="info"
          title="Removing the policy does not cancel the policy."
          description={
            <>
              {"If you'd like to cancel your policy, "}
              <MSSLink to="/my/support">
                contact your insurance representative
              </MSSLink>
            </>
          }
        />
      </div>
    </Modal>
  );
};

export default UnlinkPolicyModal;
